import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Landmark, RefreshCw, Calendar, FileText, Gavel, ExternalLink, Search, Eye } from 'lucide-react';
import FilterBar from '../components/local-body/FilterBar';
import VerdictCard from '../components/local-body/VerdictCard';
import Pagination from '../components/local-body/Pagination';
import CustomDropdown from '../components/CustomDropdown';
import DevelopmentBanner from '../components/DevelopmentBanner';
import AppNavbar from '../components/AppNavbar';
import useVerdicts from '../hooks/useVerdicts';
import useMeetingMinutes from '../hooks/useMeetingMinutes';

// Local body configuration
const LOCAL_BODIES = {
  'warner-robins': {
    id: 'd1e2f3a4-5b6c-7d8e-9f0a-1b2c3d4e5f6a',
    name: 'Warner Robins City Council',
    shortName: 'City Council'
  },
  'houston-county': {
    id: 'b9d8e2a3-4c5f-6d7e-8f9a-0b1c2d3e4f5a',
    name: 'Houston County Board of Commissioners',
    shortName: 'Board of Commissioners'
  }
};

const DEFAULT_BODY_SLUG = 'warner-robins';

const parseNumberParam = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const formatDate = (date) => {
  if (!date) return 'Date unavailable';
  // Parse date as local date to avoid timezone issues
  let dateObj;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    // Parse YYYY-MM-DD format as local date (not UTC)
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    dateObj = new Date(year, month - 1, day);
  } else {
    dateObj = new Date(date);
  }
  if (isNaN(dateObj.getTime())) return 'Date unavailable';
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const MeetingCard = ({ meeting, isDarkMode }) => {
  return (
    <div
      className={`group relative rounded-xl hover:shadow-lg transition-all duration-300 border overflow-hidden ${isDarkMode ? 'border-gray-700 hover:border-blue-600 bg-gray-800/50' : 'border-gray-200 hover:border-blue-200 bg-white'}`}
      style={{ padding: '1.25rem 1.5rem' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
            <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {meeting.title || `Meeting - ${formatDate(meeting.meeting_date)}`}
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatDate(meeting.meeting_date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {meeting.source_url && (
            <a
              href={meeting.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <FileText className="w-4 h-4 mr-1.5" />
              Minutes
              <ExternalLink className="w-3 h-3 ml-1.5 opacity-60" />
            </a>
          )}
          {meeting.agenda_url && (
            <a
              href={meeting.agenda_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <FileText className="w-4 h-4 mr-1.5" />
              Agenda
              <ExternalLink className="w-3 h-3 ml-1.5 opacity-60" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};


const LocalBody = ({ onBackToHome, isDarkMode, setIsDarkMode, featureControls }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { bodySlug } = useParams();
  const [activeTab, setActiveTab] = useState('meetings');
  
  // Resolve body from URL slug or default
  const currentBodySlug = bodySlug && LOCAL_BODIES[bodySlug] ? bodySlug : DEFAULT_BODY_SLUG;
  const currentBody = LOCAL_BODIES[currentBodySlug];
  const bodyId = currentBody.id;

  // Verdicts query state (for verdicts tab)
  const verdictQueryState = useMemo(() => ({
    page: parseNumberParam(searchParams.get('vPage'), 1),
    limit: parseNumberParam(searchParams.get('vLimit'), 10),
    search: searchParams.get('vSearch') || '',
    outcome: searchParams.get('vOutcome') || 'all',
    date: searchParams.get('vDate') || ''
  }), [searchParams]);

  // Meetings query state (for meetings tab)
  const meetingQueryState = useMemo(() => ({
    page: parseNumberParam(searchParams.get('mPage'), 1),
    limit: parseNumberParam(searchParams.get('mLimit'), 10),
    search: searchParams.get('mSearch') || '',
    date: searchParams.get('mDate') || ''
  }), [searchParams]);

  const updateSearchParams = useCallback((updates, prefix = 'v') => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      const paramKey = `${prefix}${key.charAt(0).toUpperCase() + key.slice(1)}`;
      if (value === undefined || value === null || value === '' || value === 'all') {
        nextParams.delete(paramKey);
      } else {
        nextParams.set(paramKey, String(value));
      }
    });

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const itemsPerPageOptions = useMemo(() => (
    [
      { value: 10, label: '10 per page' },
      { value: 25, label: '25 per page' },
      { value: 50, label: '50 per page' }
    ]
  ), []);

  const outcomeOptions = useMemo(() => (
    [
      { value: 'all', label: 'All outcomes' },
      { value: 'approved', label: 'Approved' }
    ]
  ), []);

  // Verdict handlers
  const handleVerdictSearchChange = (value) => {
    updateSearchParams({ search: value, page: 1 }, 'v');
  };

  const handleVerdictItemsPerPageChange = (value) => {
    updateSearchParams({ limit: value, page: 1 }, 'v');
  };

  const handleVerdictOutcomeChange = (value) => {
    updateSearchParams({ outcome: value, page: 1 }, 'v');
  };

  const handleVerdictDateChange = (value) => {
    updateSearchParams({ date: value, page: 1 }, 'v');
  };

  const handleVerdictPageChange = (page) => {
    updateSearchParams({ page }, 'v');
  };

  // Meeting handlers
  const handleMeetingPageChange = (page) => {
    updateSearchParams({ page }, 'm');
  };

  const handleMeetingSearchChange = (value) => {
    updateSearchParams({ search: value, page: 1 }, 'm');
  };

  const handleMeetingItemsPerPageChange = (value) => {
    updateSearchParams({ limit: value, page: 1 }, 'm');
  };

  const handleMeetingDateChange = (value) => {
    updateSearchParams({ date: value, page: 1 }, 'm');
  };

  const verdictFilters = useMemo(() => ({
    page: verdictQueryState.page,
    limit: verdictQueryState.limit,
    search: verdictQueryState.search,
    outcome: verdictQueryState.outcome,
    date: verdictQueryState.date
  }), [verdictQueryState]);

  const meetingFilters = useMemo(() => ({
    page: meetingQueryState.page,
    limit: meetingQueryState.limit,
    search: meetingQueryState.search,
    date: meetingQueryState.date
  }), [meetingQueryState]);

  // Fetch both meeting minutes and verdicts
  const { meetingMinutes, total: meetingsTotal, pagination: meetingsPagination, loading: meetingsLoading, error: meetingsError, refetch: refetchMeetings } = useMeetingMinutes(bodyId, meetingFilters);
  const { verdicts, total: verdictsTotal, pagination: verdictsPagination, loading: verdictsLoading, error: verdictsError, refetch: refetchVerdicts } = useVerdicts(bodyId, verdictFilters);

  const verdictTotalPages = verdictsPagination?.totalPages || 1;
  const verdictPageSize = verdictsPagination?.itemsPerPage || verdictQueryState.limit;
  const verdictStartItem = verdictsTotal === 0 ? 0 : (verdictQueryState.page - 1) * verdictPageSize + 1;
  const verdictEndItem = verdictsTotal === 0 ? 0 : Math.min(verdictQueryState.page * verdictPageSize, verdictsTotal);

  const meetingTotalPages = meetingsPagination?.totalPages || 1;
  const meetingPageSize = meetingsPagination?.limit || meetingQueryState.limit;
  const meetingStartItem = meetingsTotal === 0 ? 0 : (meetingQueryState.page - 1) * meetingPageSize + 1;
  const meetingEndItem = meetingsTotal === 0 ? 0 : Math.min(meetingQueryState.page * meetingPageSize, meetingsTotal);

  const SkeletonCard = () => (
    <div
      className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-6 animate-pulse`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className={`h-5 w-20 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        <div className={`h-5 w-28 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        <div className={`h-4 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      </div>
      <div className={`h-6 w-3/4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-3`} />
      <div className={`h-4 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`} />
    </div>
  );

  const bodyTitle = currentBody.name;

  return (
    <div className={`min-h-screen w-full overflow-x-hidden ${isDarkMode ? 'bg-gray-900 dark' : 'bg-gray-50'}`}>
      <AppNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} featureControls={featureControls} />
      <DevelopmentBanner isDarkMode={isDarkMode} />

      <div className="max-w-7xl mx-auto px-4 pt-4">
        <div className={`inline-flex rounded-lg p-1 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <button
            onClick={() => navigate('/local-body/warner-robins')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              currentBodySlug === 'warner-robins'
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            City Council
          </button>
          <button
            onClick={() => navigate('/local-body/houston-county')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              currentBodySlug === 'houston-county'
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            County Commission
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-6 md:py-10 w-full">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-8">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${isDarkMode
                ? 'bg-gray-700'
                : 'bg-gradient-to-r from-red-100 to-blue-100'
                } mb-6`}
            >
              <Landmark className={`w-8 h-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
            </div>
            <h1
              className={`text-3xl md:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}
            >
              {bodyTitle}
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Track meeting minutes, decisions, and voting outcomes
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className={`inline-flex items-center rounded-xl p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <button
                onClick={() => setActiveTab('meetings')}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === 'meetings'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                    : isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Meetings ({meetingsTotal})
              </button>
              <button
                onClick={() => setActiveTab('verdicts')}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === 'verdicts'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                    : isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Gavel className="w-4 h-4" />
                Verdicts ({verdictsTotal})
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Meetings Tab */}
      {activeTab === 'meetings' && (
        <>
          {/* Meeting Filter Bar */}
          <section className="pb-4 w-full">
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
              <div
                className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} mb-6`}
              >
                <div className="text-center mb-6">
                  <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Showing {meetingStartItem}-{meetingEndItem} of {meetingsTotal} meetings
                  </p>
                </div>

                {/* Search Input */}
                <div className="relative mb-6">
                  <Search
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-5 h-5`}
                  />
                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={meetingQueryState.search}
                    onChange={(e) => handleMeetingSearchChange(e.target.value)}
                    className={`w-full pl-12 pr-6 py-3 text-base border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>

                {/* Filter Dropdowns Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                      <Eye className="w-4 h-4 mr-2" />
                      Show per page
                    </label>
                    <CustomDropdown
                      value={meetingQueryState.limit}
                      onChange={handleMeetingItemsPerPageChange}
                      options={itemsPerPageOptions}
                      placeholder="Select items per page"
                      isDarkMode={isDarkMode}
                      icon={Eye}
                    />
                  </div>

                  <div>
                    <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                      <Calendar className="w-4 h-4 mr-2" />
                      From Date
                    </label>
                    <div className={`flex items-center px-4 py-3 border rounded-xl shadow-sm ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                      <Calendar className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />
                      <input
                        type="date"
                        value={meetingQueryState.date}
                        onChange={(e) => handleMeetingDateChange(e.target.value)}
                        className={`w-full bg-transparent text-sm focus:outline-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                      />
                      {meetingQueryState.date && (
                        <button
                          type="button"
                          onClick={() => handleMeetingDateChange('')}
                          className={`text-xs font-semibold ml-2 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-8 md:pb-12 w-full">
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
              {meetingsLoading && (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <SkeletonCard key={`skeleton-meeting-${idx}`} />
                  ))}
                </div>
              )}

              {!meetingsLoading && meetingsError && (
                <div className="text-center py-12">
                  <Calendar className={`w-16 h-16 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                    Unable to load meeting minutes
                  </h3>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{meetingsError}</p>
                  <button
                    type="button"
                    onClick={refetchMeetings}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </button>
                </div>
              )}

              {!meetingsLoading && !meetingsError && meetingMinutes.length > 0 && (
                <div className="space-y-3">
                  {meetingMinutes.map((meeting) => (
                    <MeetingCard key={meeting.id} meeting={meeting} isDarkMode={isDarkMode} />
                  ))}
                </div>
              )}

              {!meetingsLoading && !meetingsError && meetingMinutes.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className={`w-16 h-16 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                    No meeting minutes found
                  </h3>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                    {meetingQueryState.search || meetingQueryState.date
                      ? 'Try adjusting your search criteria or filters.'
                      : 'Meeting minutes will appear here once they are uploaded.'}
                  </p>
                </div>
              )}

              {/* Meeting Pagination */}
              {!meetingsLoading && !meetingsError && meetingsTotal > 0 && (
                <Pagination
                  currentPage={meetingQueryState.page}
                  totalPages={meetingTotalPages}
                  onPageChange={handleMeetingPageChange}
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </section>

        </>
      )}

      {/* Verdicts Tab */}
      {activeTab === 'verdicts' && (
        <>
          <section className="pb-4 w-full">
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
              <div
                className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} mb-6`}
              >
                <div className="text-center mb-6">
                  <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Showing {verdictStartItem}-{verdictEndItem} of {verdictsTotal} verdicts
                  </p>
                </div>

                <FilterBar
                  isDarkMode={isDarkMode}
                  searchTerm={verdictQueryState.search}
                  onSearchChange={handleVerdictSearchChange}
                  itemsPerPage={verdictQueryState.limit}
                  onItemsPerPageChange={handleVerdictItemsPerPageChange}
                  outcomeFilter={verdictQueryState.outcome}
                  onOutcomeChange={handleVerdictOutcomeChange}
                  dateFilter={verdictQueryState.date}
                  onDateChange={handleVerdictDateChange}
                  itemsPerPageOptions={itemsPerPageOptions}
                  outcomeOptions={outcomeOptions}
                />
              </div>
            </div>
          </section>

          <section className="pb-8 md:pb-12 w-full">
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
              {verdictsLoading && (
                <div className="space-y-4">
                  {Array.from({ length: Math.min(verdictQueryState.limit, 6) }).map((_, idx) => (
                    <SkeletonCard key={`skeleton-verdict-${idx}`} />
                  ))}
                </div>
              )}

              {!verdictsLoading && verdictsError && (
                <div className="text-center py-12">
                  <Gavel className={`w-16 h-16 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                    Unable to load verdicts
                  </h3>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{verdictsError}</p>
                  <button
                    type="button"
                    onClick={refetchVerdicts}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </button>
                </div>
              )}

              {!verdictsLoading && !verdictsError && verdicts.length > 0 && (
                <div className="space-y-4">
                  {verdicts.map((verdict) => (
                    <VerdictCard key={verdict.id || verdict._id} verdict={verdict} isDarkMode={isDarkMode} />
                  ))}
                </div>
              )}

              {!verdictsLoading && !verdictsError && verdicts.length === 0 && (
                <div className="text-center py-12">
                  <Gavel className={`w-16 h-16 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                    No verdicts found
                  </h3>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                    Try adjusting your search criteria or filters.
                  </p>
                </div>
              )}

              {!verdictsLoading && !verdictsError && (
                <Pagination
                  currentPage={verdictQueryState.page}
                  totalPages={verdictTotalPages}
                  onPageChange={handleVerdictPageChange}
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default LocalBody;
